import { Add, ExpandMore, List } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Divider, Grid2, Typography } from '@mui/material';

import FormikAutocomplete from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { FormikCheckbox } from '@/common/presentation/components/forms/fields/FormikCheckbox';
import { FormikTextField } from '@/common/presentation/components/forms/fields/FormikTextField';
import { JobSiteFormData } from '@/jobSites/application/types/JobSiteTypes';
import LocationForm from '@/map/presentation/LocationForm';
import tid from '@/common/infrastructure/config/tid';
import { useJobSiteFormData } from '@/jobSites/presentation/hooks/useJobSiteFormData';
import { useListCustomersAutocomplete } from '@/jobs/application/adapters/listCustomersAutocomplete';
import { useNavigate } from 'react-router';
import { useState, useEffect, useMemo, type FunctionComponent } from 'react';
import { useFormikContext } from 'formik';
import CustomerModal from '@/customers/presentation/pages/CustomersPage/CustomersManagementTab/CustomerModal';
import { formatPhoneNumber } from '@/common/application/utils/phoneUtils';

interface JobSiteDrawerFormFieldsProps {
  onInputActionClicked?: () => void;
}

const JobSiteDrawerFormFields: FunctionComponent<JobSiteDrawerFormFieldsProps> = ({ onInputActionClicked }) => {
  const navigate = useNavigate();
  const { initialValues, values, setFieldValue, setFieldTouched, validateField } = useFormikContext<JobSiteFormData>();
  const { isNew, availableJobSiteTypes, formOptionsJobSiteTypes, defaultJobSiteTypeId } = useJobSiteFormData(initialValues.id);
  const [customerSearchTerm, setCustomerSearchTerm] = useState(initialValues.customerCompanyName || '');
  const [isCreateCustomerModalOpen, setIsCreateCustomerModalOpen] = useState(false);
  // Determine if customer field should be disabled (when editing OR when pre-filled during creation)
  const isCustomerFieldDisabled = !isNew || !!(initialValues.customerId && initialValues.customerCompanyName);

  const [currentCustomerOption, setCurrentCustomerOption] = useState<
    { value: string | number; label: string; email?: string | null; phone?: string | null } | undefined
  >(
    initialValues.customerId && initialValues.customerCompanyName
      ? {
          value: initialValues.customerId,
          label: initialValues.customerCompanyName,
          email: null,
          phone: null
        }
      : undefined
  );
  const {
    options: customerOptions,
    loading: customerLoading,
    filterOptions: customerFilterOptions
  } = useListCustomersAutocomplete({ searchTerm: customerSearchTerm });

  const jobSiteTypeOptions = useMemo(
    () =>
      isNew
        ? (formOptionsJobSiteTypes?.map((type) => ({ value: type.id, label: type.objectName })) ?? [])
        : (availableJobSiteTypes?.map((type) => ({ value: type.id, label: type.objectName })) ?? []),
    [isNew, formOptionsJobSiteTypes, availableJobSiteTypes]
  );

  // Apply ERP default job site type when creating a new job site and no type is selected yet
  useEffect(() => {
    if (isNew && defaultJobSiteTypeId && !values.jobSiteTypeId) {
      setFieldValue('jobSiteTypeId', defaultJobSiteTypeId);
    }
  }, [isNew, defaultJobSiteTypeId, values.jobSiteTypeId]);

  // Sync customer option state when initialValues change (e.g., when modal opens with pre-filled customer)
  useEffect(() => {
    if (initialValues.customerId && initialValues.customerCompanyName) {
      const customerOption = {
        value: initialValues.customerId,
        label: initialValues.customerCompanyName,
        email: null,
        phone: null
      };
      setCurrentCustomerOption(customerOption);
      setCustomerSearchTerm(initialValues.customerCompanyName);
      // Ensure Formik values are set when pre-filled
      setFieldValue('customerId', initialValues.customerId);
      setFieldValue('customerCompanyName', initialValues.customerCompanyName);
    } else if (!initialValues.customerId) {
      setCurrentCustomerOption(undefined);
      setCustomerSearchTerm('');
    }
  }, [initialValues.customerId, initialValues.customerCompanyName]);

  const handleCustomerInputChange = (_: React.SyntheticEvent, value: string) => {
    setCustomerSearchTerm(value);
  };

  const handleCreateCustomerSuccess = (customer: { id: number; companyName: string }) => {
    // Create a new option with the customer data
    const newCustomerOption = {
      value: customer.id,
      label: customer.companyName,
      email: null,
      phone: null
    };

    // Set the current customer option to display in the autocomplete
    setCurrentCustomerOption(newCustomerOption);

    // Set the search term to the new customer's name to trigger a search
    setCustomerSearchTerm(customer.companyName);

    // Set the form value to the new customer's ID
    setFieldValue('customerId', customer.id);
    setFieldValue('customerCompanyName', customer.companyName);
    // Mark field as touched and validate to clear any required errors
    setFieldTouched('customerId', true);
    validateField('customerId');
    setIsCreateCustomerModalOpen(false);
  };

  return (
    <Grid2 container spacing={1} sx={{ padding: 3, paddingTop: '12px' }}>
      {!isNew && (
        <Grid2 size={12}>
          <FormikTextField<JobSiteFormData> id="id" label="ID" disabled testId="job-sites:management:id-input" />
        </Grid2>
      )}

      <Grid2 size={12}>
        <FormikAutocomplete<{ value: string | number; label: string; email?: string | null; phone?: string | null }, JobSiteFormData>
          id="customerId"
          required
          label="Customer"
          {...tid('job-sites:management:customer-autocomplete')}
          options={customerOptions}
          optionsloading={customerLoading}
          filterOptions={customerFilterOptions}
          onInputChange={handleCustomerInputChange}
          getOptionLabel={(option) => option?.label}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          value={currentCustomerOption}
          disabled={isCustomerFieldDisabled}
          actions={[
            {
              label: 'View All Customers',
              icon: <List />,
              onClick: () => {
                onInputActionClicked?.();
                navigate('/customers');
              }
            },
            {
              label: 'Add New Customer',
              icon: <Add />,
              onClick: () => {
                // Don't call onInputActionClicked here - it would close the JobSite modal
                // We want to keep the JobSite modal open while the Customer modal is open
                setIsCreateCustomerModalOpen(true);
              }
            }
          ]}
        />
      </Grid2>

      <Grid2 size={12}>
        <FormikTextField<JobSiteFormData> id="jobSiteName" label="Job Site Name" testId="job-sites:management:name-input" />
      </Grid2>

      <Grid2 size={12}>
        <FormikAutocomplete<{ value: number; label: string }, JobSiteFormData>
          id="jobSiteTypeId"
          label="Job Site Type"
          {...tid('job-sites:management:type-autocomplete')}
          required
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onInputChange={() => {}}
          options={jobSiteTypeOptions}
        />
      </Grid2>

      <Grid2 size={12}>
        <FormikCheckbox<JobSiteFormData>
          id="powerlinePresent"
          label="Has Powerline Present"
          {...tid('job-sites:management:powerline-checkbox')}
        />
      </Grid2>

      <Grid2 size={12}>
        <FormikTextField<JobSiteFormData>
          id="jobPhone"
          label="Job Site Phone"
          testId="job-sites:management:job-phone-input"
          placeholder="(XXX) XXX-XXXX"
          transformOnChange={formatPhoneNumber}
          slotProps={{
            htmlInput: {
              inputMode: 'tel',
              maxLength: 14
            }
          }}
        />
      </Grid2>

      <Divider sx={{ marginY: 3 }} />

      <Grid2 size={12}>
        <LocationForm hideCountry={true} hideCoordinates={true} />
      </Grid2>

      {/* Notes */}
      <Grid2 size={12}>
        <Divider sx={{ marginY: 3 }} />
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="notes-content"
            id="notes-header"
            {...tid('job-sites:management:notes:accordion-summary')}
          >
            <Typography variant="subtitle1">Notes</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormikTextField<JobSiteFormData> id="notes" label="Notes" multiline rows={4} testId="job-sites:management:notes:input" />
          </AccordionDetails>
        </Accordion>
      </Grid2>

      {/* Special Instructions */}
      <Grid2 size={12}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="special-instructions-content"
            id="special-instructions-header"
            {...tid('job-sites:management:special-instructions:accordion-summary')}
          >
            <Typography variant="subtitle1">Special Instructions</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormikTextField<JobSiteFormData>
              id="specialInstructions"
              label="Special Instructions"
              multiline
              rows={4}
              testId="job-sites:management:special-instructions:input"
            />
          </AccordionDetails>
        </Accordion>
      </Grid2>

      <CustomerModal
        open={isCreateCustomerModalOpen}
        onClose={() => setIsCreateCustomerModalOpen(false)}
        onSuccess={handleCreateCustomerSuccess}
      />
    </Grid2>
  );
};

export default JobSiteDrawerFormFields;
